// Import the patch before using react-virtualized components
import "./react-virtualized-patch";
import "./main.css";
import appStores from "./appStoresConfig";
// @intrasync/designer (and other @intrasync/* packages) inject stores via
// @intrasync/react's OWN injectStores, which keeps a separate allowlist from the
// host's local injectStores. That library allowlist stays empty until
// @intrasync/react's setupInjectStores is called, so library-injected components
// (e.g. the designer header/body) can't resolve host-registered view stores like
// intraSyncDesignerViewStore. Populate it with the same store set as the host's.
import { setupInjectStores as setupLibraryInjectStores } from "@intrasync/react";
import serviceStores from "serviceStoresConfig";

import { pwaRegistration } from "./pwaUtils";
import { renderApp } from "./renderApp";
import { createRootStore } from "./createRootStore";

const rootStore = createRootStore();

pwaRegistration(rootStore);

// WORKAROUND (@intrasync/stores incremental store loading race) —
// see docs/bug-reports/2026-06-26-rootstore-incremental-store-loading-race.md
//
// RootStore loads its ~338 service stores in batches of 60 but only EAGERLY
// initializes the first 2 batches (120 stores). The remaining batches — which
// include holdStore (index ~181, batch 3) and ~217 other stores — are mounted
// later from a fragile `setTimeout(10ms)` deferred init inside the library.
//
// Meanwhile @intrasync/react's injectStores allowlist is populated with ALL
// store names immediately, so any component that injects a deferred store
// (e.g. the Production Floor Map's panel cards inject `holdStore`) — or any
// route hook that touches one (castingMap.Route onEnter calls
// `holdStore.find()`) — throws "MobX injector: Store 'holdStore' is not
// available" if it renders inside that ~90ms window. On a warm PWA load with a
// cached auth token the router reaches a deep-linked route well within the gap,
// and the ErrorBoundary stays broken even after the store mounts.
//
// Fix: force every batch to mount BEFORE the first render so no deferred store
// is ever missing when components/route hooks resolve it. `initializeBatch` is
// idempotent (skips already-initialized batches), so this cooperates safely
// with the library's own eager + deferred initialization.
const ensureAllServiceStores = rootStore.serviceStoreManager
  ? rootStore.serviceStoreManager.ensureAllBatchesInitialized()
  : Promise.resolve();

Promise.all([
  setupLibraryInjectStores(appStores, serviceStores),
  ensureAllServiceStores,
])
  .then(() => {
    // Stores setup complete (host + @intrasync/react allowlists) and every
    // service-store batch is mounted on the rootStore.
    renderApp(rootStore);
  })
  .catch((error) => {
    console.error("Error setting up stores", error);
    // Render anyway so a batch-init failure degrades gracefully instead of
    // leaving a blank screen.
    renderApp(rootStore);
  });
